import type { CommandEntryInputDto, RequirementLineInputDto } from '@pams/shared';
import { WHOLE_DAY_SLOT_ID } from '@pams/shared';
import type { ArrangementFormValues, DutyPointFormValue } from '@/pages/ArrangementBuilder/formValues';
import { instructionIsReal } from '@/pages/ArrangementBuilder/autosave';

/**
 * ─── THE INTAKE'S WATCHED DRAFT ──────────────────────────────────────────────────
 * The JSON-serialisable projection of an intake form that every shared control reads, and
 * the shift row it is built from. It sits beside `Sections.tsx` because both intake forms
 * render those sections and both must watch the same shape.
 *
 * ⚠️ WHAT DOES *NOT* LIVE HERE: `ArrangementFormValues` and the two crossings that read it
 * (`toSchedule`, `fromArrangement`). Those are the PA arrangement's wire shape, and the
 * round-the-clock form has a different one.
 */

export type TimingKind = 'NO_FIXED_TIME' | 'SINGLE' | 'MULTIPLE';
export type RecurrenceKind = 'ONE_DAY' | 'DATE_RANGE' | 'WEEKDAYS';
/** ⚠️ WHEN it stops, not WHAT repeats. See `ArrangementFormValues.endsKind`. */
export type RecurrenceEndKind = 'ON_DATE' | 'AFTER_N';

export type ShiftRow = { id: string; name?: string | null; startTime: string; endTime: string };

/**
 * ⚠️ A SHIFT ID IS MINTED HERE, ON THE CLIENT, AND NEVER RECOMPUTED.
 *
 * It is what `slotKey` is built from, so a positional id would silently re-point every
 * slot override the moment a shift is inserted above another. The server accepts an absent
 * id and mints its own, exactly as `rekeyCommandRoles` does for a command role — this just
 * means the strip and the overrides agree before the first save.
 *
 * ⚠️ `getRandomValues`, NEVER `randomUUID`. `crypto.randomUUID` IS SECURE-CONTEXT ONLY: it
 * is `undefined` over plain `http://` to anything that is not `localhost`, so the create
 * page threw `crypto.randomUUID is not a function` the first time it was opened from
 * another machine — which is the demo case. `crypto.getRandomValues` carries no such
 * restriction and is available in every context.
 *
 * ⚠️ AND NO TEST COULD HAVE SEEN IT. Node and jsdom both expose `randomUUID`
 * unconditionally, with no secure-context rule at all, so a green suite says nothing about
 * this. `formValues.test.ts` stubs it to `undefined` to encode that; CLAUDE.md §8 now bans
 * the whole family.
 *
 * Four bytes is eight hex characters — unique enough for at most six shifts on one order,
 * and short enough to read in a slot key, a URL or a log line.
 */
export const mintShiftId = (): string =>
  [...crypto.getRandomValues(new Uint8Array(4))].map((byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * ⚠️ [amended D4] A DUTY POINT'S IDENTITY, MINTED THE SAME WAY AND FOR THE SAME REASON.
 *
 * `deployments.duty_point_id` reads it and `applyArrangementContent` replaces the whole
 * array on every save, so anything derived from WHERE a card sits re-points every duty the
 * moment one is inserted above another. Same rule as `mintShiftId`, one level up — and the
 * same `getRandomValues`, because `crypto.randomUUID` is SECURE-CONTEXT ONLY and no test
 * can see that (CLAUDE.md §8, divergence #28).
 */
export const mintDutyPointId = mintShiftId;

export const DEFAULT_SHIFT = (): ShiftRow => ({ id: mintShiftId(), startTime: '08:00', endTime: '20:00' });

/**
 * The default second shift is a NIGHT one, and that is not a whim: the overwhelming
 * majority of two-shift bandobast is day/night, so the operator's most likely next action
 * is to accept it. It is also the case that exercises the `+1 day` badge, so the control
 * explains itself the first time it is used.
 */
export const DEFAULT_NIGHT_SHIFT = (): ShiftRow => ({ id: mintShiftId(), startTime: '20:00', endTime: '08:00' });

/**
 * What the whole right column watches.
 *
 * ⚠️ EVERY VALUE HERE IS JSON-SERIALISABLE, AND THAT IS LOAD-BEARING RATHER THAN TIDY.
 * rc-field-form decides whether to re-render by comparing `JSON.stringify` of the selected
 * value and FALLS BACK TO `Math.random()` WHEN THAT THROWS — so returning a luxon
 * `DateTime`, a `Map` or a `ZodError` here makes the preview re-render on every keystroke
 * of every field, for ever, and nobody ever finds out why. `paDate` is therefore an ISO
 * STRING, not the `DateTime` the picker holds. `PolicyBuilder`'s header records the same
 * trap; this is the second consumer to meet it.
 */
export type ArrangementDraft = {
  policyId: string;
  /**
   * ⚠️ [amended C1a] THE WATCHED DRAFT IS A SECOND REGISTERED-FIELDS BOUNDARY, AND THIS
   * FIELD'S ABSENCE SILENTLY DEFEATED A SHIPPED FIX.
   *
   * [PRE-P6] §3a made the builder fetch a version PER CARD — "its own when it has one, the
   * policy's current when it does not" — and wrote `versionRefFor` to read
   * `point.policyVersionId ?? currentVersionId`. But every RENDER path iterates
   * `draft.dutyPoints`, and `toDutyPointDraft` did not project this key. So the left branch
   * was unreachable on every render: an arrangement raised on v1 resolved against v2, its
   * label read `v2`, a column v2 had dropped rendered `—`, and the fork notice fired on an
   * order nobody had edited.
   *
   * CLAUDE.md §8 already records that `Form.useWatch`'s selector sees REGISTERED fields
   * only. This is the same hazard one level along: a derivation can only see what the
   * PROJECTION carries, and neither the compiler nor any test could see the difference.
   */
  policyVersionId: string;
  /** ⚠️ See `DutyPointFormValue.columnsFromVersionId` — the columns' provenance, not the binding. */
  columnsFromVersionId: string;
  paDate: string;
  title: string;
  locationNames: string[];
  placeCount: number;
  oneAfterAnother: boolean;
  timingKind: TimingKind;
  /**
   * ⚠️ [C3a §6] THE OPERATOR'S TYPED REPORTING TIME — AND IT HAS TO BE **PROJECTED**, NOT JUST
   * DECLARED.
   *
   * The card's summary line reads `From 3:00 PM` where a whole-day duty has one. This is the
   * watched draft, which is a PROJECTION: a field it does not carry is invisible to every
   * derivation that reads it, however correct that derivation looks. [C1a] lost a whole release
   * to exactly this — `policyVersionId` was resolved per card and `toDutyPointDraft` did not
   * project it, so the pinned branch was unreachable on every render and neither the compiler
   * nor any test could see it.
   */
  reportingTime: string | null;
  /** [STAND-DOWN] The optional end of a whole-day duty. PROJECTED, for `reportingTime`'s reason above. */
  standDownTime: string | null;
  /**
   * ⚠️ [C4a §4] THE CARD'S ZONE — PROJECTED, for `reportingTime`'s reason above. The zone field
   * autocompletes from the OTHER cards' drafts, so a zone this projection dropped would be a zone
   * no other card could ever offer.
   */
  zone: string | null;
  /** [C5c §4] THE CARD'S SECTOR — PROJECTED, for the zone's reason: the other cards offer it back. */
  sector: string | null;
  recurrenceKind: RecurrenceKind;
  shifts: ShiftRow[];
  weekdays: number[];
  startDate: string;
  endsKind: RecurrenceEndKind;
  endDate: string;
  occurrences: number | undefined;
  commandEntries: CommandEntryInputDto[];
  /**
   * ⚠️ THE LINES THEMSELVES, NOT JUST THEIR TOTALS.
   *
   * The grid and the right column both derive from them, and reading them out of the form
   * with `getFieldValue` instead would be a NON-REACTIVE read inside a `useMemo` — it
   * returns a fresh array every render, so the memo never settles, and it does not change
   * identity when the value does, so the memo goes stale. Watched, it does both correctly.
   */
  requirementLines: RequirementLineInputDto[];
  requirementTotals: number[];
  instructionCount: number;
};

/**
 * ⚠️ [amended D4] ONE DRAFT PER DUTY POINT, AND `ArrangementDraft` IS UNCHANGED.
 *
 * Keeping the shape byte-identical is what lets `PlacesSection`, `TimingSection`,
 * `CommandSection` and `ForceGrid` move down a level without a single prop-type change —
 * each card projects to exactly the draft they already read. `placeCount` and
 * `locationNames` describe THAT CARD's places; `paDate` is carried on every one because a
 * card with no schedule of its own inherits the order date.
 */
/** One row of a places list, in either form's shape. */
export type PlaceRow = { placeId: string | null; name: string };

/**
 * ⚠️ [D4.5 §2b] A DUTY POINT ALWAYS HAS A PLACE ROW. THE READ SIDE PUTS BACK WHAT THE WRITE
 * SIDE CORRECTLY STRIPS.
 *
 * Saving filters blank places out — and must keep doing so, because a persisted blank would
 * make `placeCount`, `locationNames` and the derived title all wrong, and the schema rejects
 * an empty name anyway. But the rehydrate mapped straight through with no floor, so a card
 * saved before its place was typed came back with `places: []`, and at zero rows
 * `PlacesSection` renders an `EmptyState` and no input at all.
 *
 * ⚠️ THE OPERATOR WAS THEN STUCK, AND THE PUBLISH BLOCKER TOLD THEM TO DO THE IMPOSSIBLE:
 * "add a place", beside no field to add one in. The only surviving control was a borderless
 * text link captioned "Add another stop" — the wrong word at zero — and the per-row Remove is
 * disabled at one row, so zero is unreachable by hand and nothing in the suite had ever been
 * in that state.
 *
 * ⚠️ IT CANNOT FAKE A PLACE. `toDutyPointDraft` below counts only non-blank names, so the
 * floored row leaves `placeCount` at 0 and the publish blocker still fires — correctly, and
 * now beside a field the operator can actually type into.
 *
 * Four seeders now agree on one shape: `newDutyPoint`, `duplicateDutyPoint`, this, and
 * `StandingOrderBuilder`'s `emptyStandingOrder`.
 */
export const atLeastOnePlace = (rows: readonly PlaceRow[] | undefined): PlaceRow[] =>
  rows && rows.length > 0
    ? rows.map((row) => ({ placeId: row.placeId, name: row.name }))
    : [{ placeId: null, name: '' }];

export const toDutyPointDraft = (raw: unknown, paDate: string): ArrangementDraft => {
  const point = (raw ?? {}) as Partial<DutyPointFormValue>;
  const places = point.places ?? [];

  return {
    policyId: point.policyId ?? '',
    policyVersionId: point.policyVersionId ?? '',
    columnsFromVersionId: point.columnsFromVersionId ?? '',
    paDate,
    title: '',
    locationNames: places.map((row) => row?.name ?? '').filter((name) => name.length > 0),
    placeCount: places.filter((row) => (row?.name ?? '').trim().length > 0).length,
    /* ⚠️ [amended D4] Vestigial on a card: its places ARE its route. Never rendered. */
    oneAfterAnother: false,
    timingKind: point.timingKind ?? 'NO_FIXED_TIME',
    /* ⚠️ [C3a §6] PROJECTED — see the field's own comment. Declaring it is half the job. */
    reportingTime: point.reportingTime ?? null,
    standDownTime: point.standDownTime ?? null,
    zone: point.zone ?? null,
    sector: point.sector ?? null,
    recurrenceKind: point.recurrenceKind ?? 'ONE_DAY',
    shifts: point.shifts ?? [],
    weekdays: point.weekdays ?? [],
    startDate: point.startDate?.toISODate() ?? '',
    endsKind: point.endsKind ?? 'ON_DATE',
    endDate: point.endDate?.toISODate() ?? '',
    occurrences: point.occurrences,
    /*
     * ⚠️ [C6a] WHOLESALE, AND THAT IS WHAT CARRIES A ROLE'S ASKS — the [C1a] trap is a projection that
     * enumerates fields and silently drops one a derivation reads (the card's ask summary, the rail's
     * "asked of", the autosave gate). Enumerate here and the asks must be named in the same commit.
     */
    commandEntries: point.commandEntries ?? [],
    requirementLines: point.requirementLines ?? [],
    requirementTotals: (point.requirementLines ?? []).map((line) => line?.requiredCount ?? 0),
    /*
     * ⚠️ [amended D4.3] REAL NOW. It was hard-coded `0` from D4 because a duty point had no
     * instructions of its own; reopening OD-20 gave it some, and the field was already here
     * waiting for them.
     */
    instructionCount: (point.instructions ?? []).filter(instructionIsReal).length,
  };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §4b] THE ZONES A CARD IS OFFERED — every OTHER card's zone on THIS order, and nothing more.
 *
 *  Not a global master (none exists, and none is being created), and not the card's own value, which
 *  would offer an operator the word they are already typing. Trimmed; the first spelling of a zone
 *  wins when two cards differ only by case, so `North` and `north` are one suggestion.
 *
 *  ⚠️ [C5c §4] AND THE SECTORS BY THE SAME RULE — one private reader over the named field, so the two
 *  suggestion lists cannot drift apart, and each reads ONLY its own field: a zone is never offered as
 *  a sector.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const siblingOptionsFor = (
  points: readonly { id: string; draft: Partial<Pick<ArrangementDraft, 'zone' | 'sector'>> }[],
  exceptId: string,
  field: 'zone' | 'sector',
): string[] => {
  const byKey = new Map<string, string>();
  for (const point of points) {
    if (point.id === exceptId) continue;
    const value = (point.draft[field] ?? '').trim();
    if (value.length > 0 && !byKey.has(value.toLowerCase())) byKey.set(value.toLowerCase(), value);
  }
  return [...byKey.values()];
};

export const zoneOptionsFor = (
  points: readonly { id: string; draft: Pick<ArrangementDraft, 'zone'> }[],
  exceptId: string,
): string[] => siblingOptionsFor(points, exceptId, 'zone');

/** [C5c §4] The OTHER cards' sectors on this order — `zoneOptionsFor`'s rule, over the sector. */
export const sectorOptionsFor = (
  points: readonly { id: string; draft: Pick<ArrangementDraft, 'sector'> }[],
  exceptId: string,
): string[] => siblingOptionsFor(points, exceptId, 'sector');

/**
 * What the PAGE watches: the order's own fields, plus one card draft per duty point.
 *
 * ⚠️ EVERY VALUE IS STILL JSON-SERIALISABLE, for the reason `ArrangementDraft`'s header
 * gives — rc-field-form compares `JSON.stringify` and falls back to `Math.random()` when
 * that throws, which re-renders the whole right column on every keystroke for ever.
 */
export type ArrangementPaDraft = {
  paDate: string;
  paNumber: string;
  title: string;
  description: string;
  estimatedCrowdSize: string;
  instructionCount: number;
  dutyPoints: ArrangementDraft[];
  /** The card ids, in order — so a reorder is visible to every derivation. */
  dutyPointIds: string[];
  /**
   * ⚠️ [C5c §2] WHICH COLUMNS ARE SHARED, AND FROM WHICH CARD — PROJECTED, NEVER LEFT TO A SPREAD.
   *
   * The dash on a covered card, the anchor's toggle and the publish check all read it, and [C1a]
   * recorded what the watched draft costs when a derivation reads a key the projection forgot:
   * the branch is dead on every render and nothing says so. The stash is not projected — no
   * derivation reads it, and it would put every covered card's allocations into every render's
   * `JSON.stringify` comparison for nothing.
   */
  sharedColumns: { lineKey: string; anchorDutyPointId: string }[];
};

export const toDraft = (raw: unknown): ArrangementPaDraft => {
  const values = (raw ?? {}) as Partial<ArrangementFormValues>;
  const paDate = values.paDate?.toISODate() ?? '';
  /*
   * ⚠️ A CARD WITHOUT A MINTED `id` IS NOT A CARD — IT IS A GHOST, AND FILTERING IT HERE IS
   * THE FIX FOR A REAL REMOVAL BUG.
   *
   * rc-field-form keeps a registered field entity per PATH. Removing `dutyPoints[1]` from
   * the array does not deregister `['dutyPoints', 1, 'id']` and its siblings, so the very
   * next `getFieldsValue()` RE-MATERIALISES index 1 as an empty object — and the stack
   * rendered a second, blank card that no longer existed in the store. (`Form.List` manages
   * that lifecycle, which is why the first attempt wrapped the stack in one; that wrapper
   * then nested `PlacesSection`'s own list and broke it instead. This filter fixes the
   * removal without reintroducing the nesting.)
   *
   * An id is minted the moment a card is created, so "has no id" is exactly "is residue".
   */
  const points = (values.dutyPoints ?? []).filter((point) => Boolean(point?.id));

  return {
    paDate,
    paNumber: values.paNumber ?? '',
    title: values.title ?? '',
    description: values.description ?? '',
    estimatedCrowdSize: values.estimatedCrowdSize ?? '',
    /*
     * ⚠️ [C5c §8e] REAL LINES ONLY, AS A CARD ALREADY COUNTED. `.length` ticked the checklist's
     * Instructions row for a blank line from "Add line" — a line the save then dropped.
     */
    instructionCount: (values.instructions ?? []).filter(instructionIsReal).length,
    dutyPoints: points.map((point) => toDutyPointDraft(point, paDate)),
    dutyPointIds: points.map((point) => point?.id ?? ''),
    sharedColumns: (values.sharedColumns ?? []).map((entry) => ({
      lineKey: entry.lineKey,
      anchorDutyPointId: entry.anchorDutyPointId,
    })),
  };
};

/** The slot key a whole-day duty carries, so the strip and the overrides agree. */
export const wholeDaySlotKey = (dutyDate: string): string => `${dutyDate}#${WHOLE_DAY_SLOT_ID}`;
